import {
  ABSpinner,
  Button,
  paymentStatus,
  paymentType,
  plans,
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import React, { useEffect, useState } from 'react'
import { Card, Col, Container, Modal, Row } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  CoachingPlanInformation,
  CoachingPlanPaymentForm,
  PageHeader,
} from '../components'
import PlanPaymentStepper from '../components/PlanAndPayment/PlanPaymentStepper'
import API from '../utils/API'
import { sowRefId as sowRefIdConst } from '../utils/constants'
import { formatDate } from '../utils/date'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)

const CoachingPlanPayment = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [paymentsDetails, setPaymentsDetails] = useState([])
  const [isPaying, setIsPaying] = useState(false)
  const [sowRefLoader, setSOWRefLoader] = useState(true)
  const [staticSOWData, setStaticSOWData] = useState(null)
  const [selectedCardDetails, setSelectedCardDetails] = useState(null)
  const [showSelectedCard, setShowSelectedCard] = useState(null)
  const [step, setStep] = useState(1)
  const history = useHistory()
  const { coachingPlanId } = useParams()
  const Error = useErrorService()
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const isRetryingPayment = query.get('isRetryingPayment')
  const sowRefId = query.get(sowRefIdConst)
  const stripe = useStripe()
  const Notification = useNotificationService()
  const { user, setUser } = useUserService()
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false)
  const [isPaymentFailed, setIsPaymentFailed] = useState(false)
  const invoiceEvery =
    paymentsDetails?.invoiceEvery > 1
      ? `every ${paymentsDetails?.invoiceEvery} month`
      : `monthly`
  const userPlanChangeHandle = () => {
    if ([plans.free.code, plans.growth.code].includes(user?.planCode)) {
      setUser({ ...user, planCode: plans.coaching.code })
      return
    }
  }

  const delay = (ms) => new Promise((res) => setTimeout(res, ms))

  useEffect(() => {
    const getSubscription = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.payment.getSubscription(coachingPlanId)
        if (data) {
          if (data.isAlreadyPaid) {
            if (
              data.paymentCycle === Object.values(paymentType)[0] &&
              data.sowStatus === paymentStatus.PAYMENT_FAILED
            ) {
              setPaymentsDetails(data)
              setIsLoading(false)
            } else {
              history.replace('/home')
            }
          } else {
            setPaymentsDetails(data)
            setIsLoading(false)
          }
        }
      } catch (e) {
        setIsLoading(false)
        if (['resource_not_found', 'forbidden'].includes(e?.code)) {
          history.replace('/plans-payments')
          return
        }
        Error.showError(e)
      }
    }

    const getStaticSOWByName = async () => {
      try {
        // just to pause popup for minimum 2 seconds (UX thing)
        const [res] = await Promise.all([
          API.coachingPlan.getStaticSOW(sowRefId),
          delay(2000),
        ])
        const { data } = res
        if (data) {
          setSOWRefLoader(false)
          setStaticSOWData(data)
          localStorage.removeItem(sowRefIdConst)
        }
      } catch (e) {
        setSOWRefLoader(false)
        Error.showError(e)
        history.replace('/plans-payments')
      }
    }

    if (sowRefId) {
      setIsLoading(false)
      getStaticSOWByName()
    } else {
      setSOWRefLoader(false)
      getSubscription()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sowRefId])

  const paymentHandler = (values, isAddressObjChanged, cardId, name) => {
    if (!sowRefId) {
      paySOWHandler(values, isAddressObjChanged, cardId, name)
    } else {
      paySowWithoutCoachHandler(values, isAddressObjChanged, cardId, name)
    }
  }

  const paySowWithoutCoachHandler = async (
    values,
    isAddressObjChanged,
    cardId,
    name,
  ) => {
    setIsPaying(true)
    try {
      const { billerName, ...address } = values || {}
      const payload = {
        paymentMethodId: cardId,
        sowId: coachingPlanId,
        billerName,
        ...(isAddressObjChanged && { address }),
      }
      const { data } = await API.payment.paySowWithoutCoach(payload)
      if (data?.sow?.actionRequired) {
        let options = {
          payment_method: data?.sow?.paymentMethodId,
        }
        const { paymentIntent, error } = await stripe.confirmCardPayment(
          data?.sow?.clientSecret,
          options,
        )
        if (paymentIntent?.status === 'succeeded') {
          await API.payment.confirmPayment({
            userId: user?._id,
            tempSowId: data?.sow?.id,
            paymentIntentId: paymentIntent?.id,
            billerName: name,
          })
          setIsPaying(false)
          userPlanChangeHandle()
          setIsPaymentSuccess(true)
        } else {
          if (error?.code === 'card_declined') {
            Notification.showNotification(error?.message, 'danger')
          }
          setIsPaying(false)
          setIsPaymentFailed(true)
          setIsPaymentSuccess(false)
        }
      } else if (data?.sow?.sowStatus === Object.values(paymentStatus)[1]) {
        setIsPaymentFailed(true)
        setIsPaymentSuccess(false)
        setIsPaying(false)
        return
      } else {
        setIsPaying(false)
        setIsPaymentFailed(false)
        userPlanChangeHandle()
        setIsPaymentSuccess(true)
      }
    } catch (e) {
      setIsPaying(false)
      setIsPaymentFailed(true)
      setIsPaymentSuccess(false)
    }
  }

  const paySOWHandler = async (values, isAddressObjChanged, cardId, name) => {
    setIsPaying(true)
    const isPaymentCycle = (type) =>
      paymentsDetails?.paymentCycle ===
      Object.values(paymentType).find((v) => v.toLowerCase().includes(type))
    const shouldCallConfirmPayment = (
      paymentIntent,
      isRetryingPayment = false,
    ) => {
      const isRecurring = isPaymentCycle('recurring') // paymentType[0]
      const isOneTime = isPaymentCycle('onetime') // paymentType[1]
      const durationOfPlan = paymentsDetails?.totalDurationOfContract
      const nextChargeDate = paymentsDetails?.nextChargeDate
      const planStartDate = paymentsDetails?.planStartDate
      const hasNextCharge = !!nextChargeDate
      const isInitialRecurring = nextChargeDate === planStartDate
      return Boolean(
        paymentIntent &&
          ((isOneTime && (!isRetryingPayment || isRetryingPayment)) ||
            (isRecurring &&
              durationOfPlan === 1 &&
              !hasNextCharge &&
              (isRetryingPayment || !isRetryingPayment)) ||
            (isRecurring &&
              // durationOfPlan > 1 &&
              ((hasNextCharge && isInitialRecurring) ||
                (isRetryingPayment &&
                  !hasNextCharge &&
                  hasNextCharge &&
                  isInitialRecurring)))),
      )
    }
    const handlePaymentSuccess = async (
      data,
      paymentIntent,
      isRetryingPayment = false,
    ) => {
      if (shouldCallConfirmPayment(paymentIntent, isRetryingPayment)) {
        await API.payment.confirmPayment({
          userId: user?._id,
          sowId: coachingPlanId,
          paymentIntentId: paymentIntent?.id,
          billerName: data?.billerName || user?.fullName,
        })
      }

      localStorage.removeItem('isRetryingPayment')
      userPlanChangeHandle()
      setIsPaymentFailed(false)
      setIsPaymentSuccess(true)
    }

    const handlePaymentError = (error) => {
      setIsPaymentFailed(true)
      setIsPaymentSuccess(false)
    }

    try {
      if (isRetryingPayment) {
        const { data } = await API.payment.changeSubscriptionCard({
          subscriptionId: coachingPlanId,
          paymentMethodId: cardId,
        })

        if (data?.actionRequired) {
          const { paymentIntent, error } = await stripe.confirmCardPayment(
            data?.clientSecret,
            { payment_method: cardId },
          )
          if (paymentIntent?.status === 'succeeded') {
            await handlePaymentSuccess(data, paymentIntent, isRetryingPayment)
          } else {
            handlePaymentError(error)
          }
        } else if (data?.sow?.sowStatus === Object.values(paymentStatus)[1]) {
          setIsPaymentFailed(true)
        } else {
          await handlePaymentSuccess(data)
        }
      } else {
        const { billerName, ...address } = values || {}
        const payload = {
          paymentMethodId: cardId,
          sowId: coachingPlanId,
          billerName,
          ...(isAddressObjChanged && { address }),
        }

        const { data } = await API.payment.paySOW(payload)

        if (data?.sow?.actionRequired) {
          const { paymentIntent, error } = await stripe.confirmCardPayment(
            data?.sow?.clientSecret,
            { payment_method: data?.sow?.paymentMethodId },
          )

          if (paymentIntent?.status === 'succeeded') {
            await handlePaymentSuccess(data, paymentIntent)
          } else {
            handlePaymentError(error)
          }
        } else if (data?.sow?.sowStatus === Object.values(paymentStatus)[1]) {
          setIsPaymentFailed(true)
        } else {
          await handlePaymentSuccess(data)
        }
      }
    } catch (e) {
      setIsPaymentFailed(true)
      setIsPaymentSuccess(false)
      // Error.showError(
      //   e?.code === 'payment_fail'
      //     ? {
      //         code: e?.code,
      //         message: 'Your payment is not completed. Please try again.',
      //       }
      //     : e,
      // )
    }

    setIsPaying(false)
  }
  const renderPageHelmet = () => {
    return (
      <Helmet>
        <title>{'Coaching Plan Payment | Abroad'}</title>
        <meta name='title' content={'Coaching Plan Payment | Abroad'}></meta>
        <meta
          property='og:title'
          content={'Coaching Plan Payment | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'Coaching Plan Payment | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/plans-payments/coaching-plan/${coachingPlanId}`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/plans-payments/coaching-plan/${coachingPlanId}`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/plans-payments/coaching-plan/${coachingPlanId}`}></meta>
      </Helmet>
    )
  }

  const renderPageHeader = () => {
    return (
      <section className='custom-col-lg-8 custom-col-10 custom-offset-lg-1'>
        <PageHeader
          title='Plan Payment'
          showBackArrow={true}
          backArrowAction={() => {
            if (history.length > 1) {
              history.goBack()
            } else {
              history.push('/home')
            }
          }}
        />
      </section>
    )
  }

  if (sowRefLoader && sowRefId) {
    return (
      <>
        {renderPageHelmet()}
        <main>
          <StaticSOWLoaderModal show={sowRefLoader} />
          {renderPageHeader()}
        </main>
      </>
    )
  }

  if (isLoading) {
    return <ABSpinner />
  }

  return (
    <>
      {renderPageHelmet()}
      <main>
        {renderPageHeader()}
        <section className='mt-52px'>
          <Container
            fluid
            className='custom-col-lg-8 custom-col-10 custom-offset-lg-1'>
            <Row noGutters className='d-flex justify-content-center p-2'>
              {
                paymentsDetails?.paymentCycle ===
                Object.values(paymentType)[0] ? (
                  [paymentStatus.WAITING_FOR_PAYMENT].includes(
                    paymentsDetails?.sowStatus,
                  ) ? (
                    <Col className='px-0' lg={5} md={8} sm={10} xs={12}>
                      <div>
                        <h5 className='mb-20px s6a'>
                          Your Coaching Plan - $
                          {paymentsDetails?.upfrontFee >= 0 &&
                          paymentsDetails?.subscriptionAmount >= 0
                            ? paymentsDetails.upfrontFee +
                              paymentsDetails.subscriptionAmount
                            : '-'}
                        </h5>
                        <Card className='mb-20px px-24px py-12px card-border'>
                          <Card.Body className='p-0'>
                            <div className='mb-3px text-saffron-700 s6a'>
                              Upfront/Setup Fee - $
                              {paymentsDetails?.upfrontFee >= 0
                                ? paymentsDetails?.upfrontFee
                                : '-'}
                            </div>
                            <div className='text-saffron-700 s6a'>
                              Subscription Amount - $
                              {paymentsDetails?.subscriptionAmount >= 0
                                ? paymentsDetails?.subscriptionAmount
                                : '-'}
                            </div>
                          </Card.Body>
                        </Card>
                        <div className='mb-20px s11 text-black-70'>
                          Only $
                          {paymentsDetails?.upfrontFee >= 0
                            ? paymentsDetails?.upfrontFee
                            : '-'}{' '}
                          will be paid for the first time, as per the coaching
                          plan, $
                          {paymentsDetails?.subscriptionAmount >= 0
                            ? paymentsDetails?.subscriptionAmount
                            : '-'}{' '}
                          is required to be paid {invoiceEvery} until the plan
                          is complete!
                        </div>
                        {paymentsDetails?.nextPaymentDate &&
                          paymentsDetails?.nextChargeDate && (
                            <div className='mb-20px s11 text-black-70'>
                              Your next subscription fee of $
                              {paymentsDetails?.subscriptionAmount >= 0
                                ? paymentsDetails?.subscriptionAmount
                                : '-'}{' '}
                              will be deducted on{' '}
                              {formatDate(paymentsDetails?.nextPaymentDate)}.
                            </div>
                          )}
                        <div className='s11 text-black-70'>
                          Take your next steps on the journey of personal growth
                          and transformative leadership.
                        </div>
                      </div>
                      {step === 2 && !isPaymentSuccess && !isPaymentFailed && (
                        <SelectedCardDetails
                          showSelectedCard={showSelectedCard}
                          setStep={setStep}
                        />
                      )}
                    </Col>
                  ) : (
                    <Col className='px-0' lg={5} md={8} sm={10} xs={12}>
                      <div>
                        {paymentsDetails?.nextPaymentDate ? (
                          <>
                            {paymentsDetails?.nextChargeDate &&
                            formatDate(paymentsDetails?.nextChargeDate) ===
                              formatDate(paymentsDetails?.planStartDate) ? (
                              <>
                                <h5 className='mb-20px s6a'>
                                  Your Coaching Plan - $
                                  {paymentsDetails?.upfrontFee >= 0 &&
                                  paymentsDetails?.subscriptionAmount >= 0
                                    ? paymentsDetails.upfrontFee +
                                      paymentsDetails.subscriptionAmount
                                    : '-'}
                                </h5>
                                <Card className='mb-20px px-24px py-12px card-border'>
                                  <Card.Body className='p-0'>
                                    <div className='mb-3px text-saffron-700 s6a'>
                                      Upfront/Setup Fee - $
                                      {paymentsDetails?.upfrontFee >= 0
                                        ? paymentsDetails?.upfrontFee
                                        : '-'}
                                    </div>
                                    <div className='text-saffron-700 s6a'>
                                      Subscription Amount - $
                                      {paymentsDetails?.subscriptionAmount >= 0
                                        ? paymentsDetails?.subscriptionAmount
                                        : '-'}
                                    </div>
                                  </Card.Body>
                                </Card>
                                <div className='mb-20px s11 text-black-70'>
                                  Only $
                                  {paymentsDetails?.upfrontFee >= 0
                                    ? paymentsDetails?.upfrontFee
                                    : '-'}{' '}
                                  will be paid for the first time, as per the
                                  coaching plan, $
                                  {paymentsDetails?.subscriptionAmount >= 0
                                    ? paymentsDetails?.subscriptionAmount
                                    : '-'}{' '}
                                  is required to be paid {invoiceEvery} until
                                  the plan is complete!
                                </div>
                                <div className='mb-20px s11 text-black-70'>
                                  Your next subscription fee of $
                                  {paymentsDetails?.subscriptionAmount >= 0
                                    ? paymentsDetails?.subscriptionAmount
                                    : '-'}{' '}
                                  will be deducted on{' '}
                                  {formatDate(paymentsDetails?.nextPaymentDate)}
                                  .
                                </div>
                                <div className='s11 text-black-70'>
                                  Take your next steps on the journey of
                                  personal growth and transformative leadership.
                                </div>
                              </>
                            ) : (
                              <>
                                <h5 className='mb-20px s6a'>
                                  Your Coaching Plan - $
                                  {paymentsDetails?.upfrontFee >= 0 &&
                                  paymentsDetails?.subscriptionAmount >= 0
                                    ? paymentsDetails.upfrontFee +
                                      paymentsDetails.subscriptionAmount
                                    : '-'}
                                </h5>
                                <Card className='mb-20px px-24px py-12px card-border'>
                                  <Card.Body className='p-0'>
                                    <div className='text-saffron-700 s6a'>
                                      Subscription Amount - $
                                      {paymentsDetails?.subscriptionAmount >= 0
                                        ? paymentsDetails?.subscriptionAmount
                                        : '-'}
                                    </div>
                                  </Card.Body>
                                </Card>
                                <div className='mb-20px s11 text-black-70'>
                                  $
                                  {paymentsDetails?.subscriptionAmount >= 0
                                    ? paymentsDetails?.subscriptionAmount
                                    : '-'}{' '}
                                  is required to be paid {invoiceEvery} until
                                  the plan is complete!
                                </div>
                                <div className='mb-20px s11 text-black-70'>
                                  Your next subscription fee of $
                                  {paymentsDetails?.nextChargeAmount >= 0
                                    ? paymentsDetails?.nextChargeAmount
                                    : '-'}{' '}
                                  will be deducted on{' '}
                                  {formatDate(paymentsDetails?.nextPaymentDate)}
                                  .
                                </div>
                                <div className='s11 text-black-70'>
                                  Take your next steps on the journey of
                                  personal growth and transformative leadership.
                                </div>
                              </>
                            )}
                          </>
                        ) : paymentsDetails?.totalDurationOfContract > 1 ? (
                          <>
                            <h5 className='mb-20px s6a'>
                              Your Coaching Plan - $
                              {paymentsDetails?.upfrontFee >= 0 &&
                              paymentsDetails?.subscriptionAmount >= 0
                                ? paymentsDetails.upfrontFee +
                                  paymentsDetails.subscriptionAmount
                                : '-'}
                            </h5>
                            <Card className='mb-20px px-24px py-12px card-border'>
                              <Card.Body className='p-0'>
                                <div className='text-saffron-700 s6a'>
                                  Subscription Amount - $
                                  {paymentsDetails?.subscriptionAmount >= 0
                                    ? paymentsDetails?.subscriptionAmount
                                    : '-'}
                                </div>
                              </Card.Body>
                            </Card>
                            <div className='s11 text-black-70'>
                              Take your next steps on the journey of personal
                              growth and transformative leadership.
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <h5 className='mb-20px s6a'>
                                Your Coaching Plan - $
                                {paymentsDetails?.upfrontFee >= 0 &&
                                paymentsDetails?.subscriptionAmount >= 0
                                  ? paymentsDetails.upfrontFee +
                                    paymentsDetails.subscriptionAmount
                                  : '-'}
                              </h5>
                              <Card className='mb-20px px-24px py-12px card-border'>
                                <Card.Body className='p-0'>
                                  <div className='mb-3px text-saffron-700 s6a'>
                                    Upfront/Setup Fee - $
                                    {paymentsDetails?.upfrontFee >= 0
                                      ? paymentsDetails?.upfrontFee
                                      : '-'}
                                  </div>
                                  <div className='text-saffron-700 s6a'>
                                    Subscription Amount - $
                                    {paymentsDetails?.subscriptionAmount >= 0
                                      ? paymentsDetails?.subscriptionAmount
                                      : '-'}
                                  </div>
                                </Card.Body>
                              </Card>
                              <div className='mb-20px s11 text-black-70'>
                                Only $
                                {paymentsDetails?.upfrontFee >= 0
                                  ? paymentsDetails?.upfrontFee
                                  : '-'}{' '}
                                will be paid for the first time, as per the
                                coaching plan, $
                                {paymentsDetails?.subscriptionAmount >= 0
                                  ? paymentsDetails?.subscriptionAmount
                                  : '-'}{' '}
                                is required to be paid {invoiceEvery} until the
                                plan is complete!
                              </div>
                              <div className='s11 text-black-70'>
                                Take your next steps on the journey of personal
                                growth and transformative leadership.
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      {step === 2 && !isPaymentSuccess && !isPaymentFailed && (
                        <SelectedCardDetails
                          showSelectedCard={showSelectedCard}
                          setStep={setStep}
                        />
                      )}
                    </Col>
                  )
                ) : paymentsDetails?.paymentCycle ===
                  Object.values(paymentType)[1] ? (
                  <Col className='px-0' lg={5} md={8} sm={10} xs={12}>
                    <div>
                      <h5 className='mb-20px s6a'>
                        Your Coaching Plan - $
                        {paymentsDetails?.onetimeAmount >= 0
                          ? paymentsDetails.onetimeAmount
                          : '-'}
                      </h5>
                      <div className='s11 text-black-70'>
                        Take your next steps on the journey of personal growth
                        and transformative leadership.
                      </div>
                    </div>
                    {step === 2 && !isPaymentSuccess && !isPaymentFailed && (
                      <SelectedCardDetails
                        showSelectedCard={showSelectedCard}
                        setStep={setStep}
                      />
                    )}
                  </Col>
                ) : null //type case: both in PaymentType
              }
              <Col className='px-0' lg={2}></Col>
              <Col
                className='px-0 pt-lg-0 pt-md-4 pt-sm-4 pt-4'
                lg={5}
                md={8}
                sm={10}
                xs={12}>
                <PlanPaymentStepper
                  currentStep={step === 1 ? 1 : 2}
                  isComplete={
                    step === 2 && (isPaymentSuccess || isPaymentFailed)
                  }
                  isCoachingPlanPayment={true}
                />
                {step === 1 && (
                  <CoachingPlanInformation
                    staticSOWData={staticSOWData}
                    subscription={paymentsDetails}
                    selectedCardDetails={selectedCardDetails}
                    setSelectedCardDetails={setSelectedCardDetails}
                    onContinue={() => {
                      setStep(2)
                    }}
                    setShowSelectedCard={setShowSelectedCard}
                  />
                )}
                {step === 2 && !isPaymentSuccess && !isPaymentFailed && (
                  <>
                    <CoachingPlanPaymentForm
                      staticSOWData={staticSOWData}
                      subscription={paymentsDetails}
                      selectedCardDetails={selectedCardDetails}
                      onSuccess={paymentHandler}
                      isLoading={isPaying}
                      onBack={() => {
                        setStep(1)
                      }}
                    />
                  </>
                )}
                {step === 2 && isPaymentSuccess && (
                  <div className='text-center mt-4'>
                    <h2 className='mb-4 s2'>Thank you!</h2>
                    <div className='s6 mb-60px'>
                      Your payment details have been successfully submitted.
                      <br />
                      You'll receive a confirmation email shortly.
                    </div>
                    <Button
                      variant='saffron'
                      className='border-radius-normal btn-same-width text-white'
                      onClick={() => history.replace('/home')}>
                      <span className='s10a px-40px py-13px'>
                        BACK TO HOMEPAGE
                      </span>
                    </Button>
                  </div>
                )}
                {step === 2 && isPaymentFailed && (
                  <div className='text-center mt-4'>
                    <h2 className='mb-4 s2' style={{ color: 'red' }}>
                      Payment Failed!
                    </h2>
                    <div className='s6 mb-60px'>
                      Your payment was unsuccessful due to some technical error.
                      We suggest retrying the transaction.
                    </div>
                    <Button
                      variant='saffron'
                      className='border-radius-normal btn-same-width text-white'
                      onClick={() => {
                        setIsPaymentFailed(false)
                        // setStep(2) // or setStep(1) if you want to go back to card entry
                        history.replace('/plans-payments')
                      }}>
                      <span className='s10a px-40px py-13px'>TRY AGAIN</span>
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>
      </main>
    </>
  )
}
const SelectedCardDetails = ({
  showSelectedCard,
  setStep,
  setSelectedCardDetails,
}) => {
  if (!showSelectedCard) return null
  return (
    <div className='mt-5'>
      <h5 className='mb-2 s6a'>Complete payment with Card</h5>
      <Card className='flex flex-row justify-content-between px-4 py-3 border rounded-lg'>
        {/* <span>{showSelectedCard?.card?.display_brand}</span> */}
        <span className='mb-0 align-self-center s6'>{`**** **** **** ${showSelectedCard?.card?.last4}`}</span>
        <Button
          size='sm'
          variant='saffron'
          className='border-radius-normal text-white'
          onClick={() => setStep(1)}>
          <span className='s10a'>Change</span>
        </Button>
      </Card>
    </div>
  )
}

const StaticSOWLoaderModal = ({ show }) => {
  return (
    <Modal show={show} onHide={() => {}} centered>
      <Modal.Body className='px-4 promo-accordion'>
        <Card className='border-0'>
          <Card.Body className='px-3 d-flex flex-column'>
            We are creating customised coaching plan for you.
            <br />
            Please wait for a moment...
          </Card.Body>
        </Card>
      </Modal.Body>
    </Modal>
  )
}

const CoachingPlanPaymentWithStripe = () => {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
          },
        ],
      }}>
      <CoachingPlanPayment />
    </Elements>
  )
}

export default CoachingPlanPaymentWithStripe
